import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Map as MapIcon, List, ArrowRight, X } from "lucide-react";
import api from "@/lib/api";
import USGeoMap from "@/components/USGeoMap";
import ConfidenceBadge from "@/components/ConfidenceBadge";
import { Section } from "@/components/common";

const REGIONS = ["All", "Northeast", "South", "Midwest", "West"];
const LEGEND = [
  ["high", "High"],
  ["medium", "Moderate"],
  ["low", "Limited data"],
];
const LEGEND_SWATCH = {
  high: "#2563EB",
  medium: "rgba(37,99,235,0.55)",
  low: "#1E293B",
};

export default function StateExplorer() {
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("All");
  const [view, setView] = useState("map");
  const [hovered, setHovered] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const searchWrap = useRef(null);

  useEffect(() => { api.get("/states").then((r) => setStates(r.data)); }, []);

  useEffect(() => {
    const onDoc = (e) => { if (searchWrap.current && !searchWrap.current.contains(e.target)) setShowResults(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => states.filter((s) =>
    (region === "All" || s.region === region) &&
    (s.name.toLowerCase().includes(q.toLowerCase()) || s.abbr.toLowerCase().includes(q.toLowerCase()))
  ), [states, region, q]);

  const activeAbbrs = useMemo(() => new Set(filtered.map((s) => s.abbr)), [filtered]);
  const searchMatches = q.length > 0
    ? states.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()) || s.abbr.toLowerCase() === q.toLowerCase()).slice(0, 6)
    : [];

  return (
    <Section className="py-10 sm:py-14">
      {/* Heading */}
      <div className="mb-6">
        <p className="eyebrow mb-2">U.S. Market Explorer</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl lg:text-5xl text-white">Explore Data Center Markets</h1>
        <p className="text-slate-400 mt-2 max-w-2xl text-sm sm:text-base">Select a state on the map or from the list to view its research — reported facility activity, power, water, cooling, and sources.</p>
      </div>

      {/* Search */}
      <div ref={searchWrap} className="relative max-w-xl mb-5" data-testid="state-search-wrap">
        <div className="flex items-center gap-3 bg-[#111827] border border-white/10 rounded-full px-5 py-3.5 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/30 transition-all">
          <Search size={18} className="text-slate-400 shrink-0" />
          <input data-testid="state-search-input" value={q}
            onChange={(e) => { setQ(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            placeholder="Search by state name or abbreviation…"
            className="bg-transparent w-full text-white placeholder-slate-500 focus:outline-none" />
          {q && <button data-testid="clear-search" onClick={() => setQ("")} className="text-slate-400 hover:text-white"><X size={16} /></button>}
        </div>
        {showResults && searchMatches.length > 0 && (
          <div data-testid="search-dropdown" className="absolute z-30 mt-2 w-full bg-[#111827] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
            {searchMatches.map((s) => (
              <button key={s.abbr} data-testid={`search-match-${s.abbr}`} onClick={() => navigate(`/states/${s.abbr}`)}
                className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-slate-800 transition-colors">
                <span className="text-white">{s.name}</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{s.abbr}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Controls: regions + legend + view toggle */}
      <div className="flex flex-col gap-3 mb-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar py-0.5" data-testid="region-filters">
            {REGIONS.map((r) => (
              <button key={r} data-testid={`region-${r}`} onClick={() => setRegion(r)}
                className={`shrink-0 text-xs sm:text-sm px-3.5 py-2 rounded-full border transition-colors ${region === r
                  ? "bg-[#2563EB] border-[#2563EB] text-white" : "border-white/10 text-slate-300 hover:border-blue-500/50"}`}>{r}</button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-[#111827] border border-white/10 rounded-full p-1 shrink-0">
            <button data-testid="view-map" onClick={() => setView("map")}
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${view === "map" ? "bg-[#2563EB] text-white" : "text-slate-400 hover:text-white"}`}>
              <MapIcon size={14} /> <span className="hidden sm:inline">Map</span>
            </button>
            <button data-testid="view-list" onClick={() => setView("list")}
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${view === "list" ? "bg-[#2563EB] text-white" : "text-slate-400 hover:text-white"}`}>
              <List size={14} /> <span className="hidden sm:inline">List</span>
            </button>
          </div>
        </div>
        {/* compact legend */}
        <div className="flex items-center gap-4 text-xs text-slate-400" data-testid="map-legend">
          <span className="font-mono eyebrow">Activity</span>
          {LEGEND.map(([k, label]) => (
            <span key={k} className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm border border-white/10" style={{ background: LEGEND_SWATCH[k] }} /> {label}
            </span>
          ))}
        </div>
      </div>

      {/* MAP VIEW */}
      {view === "map" && (
        <div className="card p-4 sm:p-6 mb-8 relative">
          <USGeoMap states={states} activeAbbrs={q || region !== "All" ? activeAbbrs : null} onHover={setHovered} />
          <div className="mt-2 min-h-[20px] text-center text-sm text-slate-400" aria-live="polite">
            {hovered ? <span><span className="text-white font-medium">{hovered.name}</span> — tap to open research</span>
              : <span className="text-slate-500">Hover or tap a state to explore its data</span>}
          </div>
        </div>
      )}

      {/* RESULTS */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-xl text-white">{filtered.length} {filtered.length === 1 ? "state" : "states"}{region !== "All" ? ` · ${region}` : ""}{q ? ` · “${q}”` : ""}</h2>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="state-grid">
        {filtered.map((s) => (
          <button key={s.id} data-testid={`state-card-${s.abbr}`} onClick={() => navigate(`/states/${s.abbr}`)}
            className="card p-5 text-left hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#84CC16] group">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-display font-bold text-xl text-white">{s.name}</h3>
              <span className="font-mono text-xs text-slate-500">{s.abbr}</span>
            </div>
            <ConfidenceBadge level={s.data_confidence} />
            <p className="text-slate-400 text-sm mt-3 line-clamp-2">{s.summary}</p>
            <span className="mt-3 inline-flex items-center gap-1.5 text-[#3B82F6] group-hover:text-[#84CC16] text-sm font-semibold transition-colors">View research <ArrowRight size={14} /></span>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="sm:col-span-2 lg:col-span-3 card p-8 text-center text-slate-400">No states match your search. Try a different name or region.</div>
        )}
      </div>
    </Section>
  );
}
